import type { LigneDuPourquoi } from '../partage/api/types';
import { teinteDuProjet } from '../apparence';
import {
  ETAGE_LEGER,
  ETAGE_PLUS_LOURD,
  ETAGE_SOUS_L_EAU,
  ETAGE_SURFACE,
  LA_FLOTTAISON,
  nAutres,
  REPLIER,
  RIEN_DE_FAISABLE,
} from '../partage/vocabulaire';
import { mentionDuGalet, nomsSousLEau, type EtagesDeMaintenant } from './etagesDeMaintenant';
import './Etages.css';

type Sorte = 'surface' | 'leger' | 'plus-lourd';

/**
 * La flottaison en étages, sous le débloquant (chantier maintenant-en-etages, modification 01 ;
 * maquette du carnet `maintenant.html`, `.mare4`, décision 22 d'Igor) : Surface, Léger, Plus
 * lourd, trois galets au plus chacun, posés à des hauteurs un peu différentes, jamais alignés en
 * liste ; une fine règle de couleur à gauche nomme les étages ; puis Sous l'eau, quelques noms et
 * « + N autres », qui déplie. Le découpage est `etagesDeMaintenant`, une règle pure : ici, rien
 * ne se calcule. Un étage vide ne s'affiche pas.
 *
 * Un galet dit ce que disait la ligne : son titre, sa durée et son pourquoi en petit ; un clic
 * ouvre sa fiche.
 */
export function Etages({
  etages,
  ouvert,
  onBasculer,
  onOuvrir,
}: {
  etages: EtagesDeMaintenant;
  /** « + N autres » touché : le serveur relit avec `toutVoir`, les noms se déplient. */
  ouvert: boolean;
  onBasculer: () => void;
  onOuvrir: (id: string) => void;
}) {
  const { surface, leger, plusLourd, sousLEau, nombreSousLEau } = etages;
  const vide = surface.length + leger.length + plusLourd.length + nombreSousLEau === 0;

  return (
    <section className="etages" aria-label={LA_FLOTTAISON} data-tuto="groupe-en-attendant">
      {vide && <p className="etages-vide">{RIEN_DE_FAISABLE}</p>}
      {surface.length > 0 && (
        <Etage sorte="surface" nom={ETAGE_SURFACE} lignes={surface} onOuvrir={onOuvrir} tuto="groupe-prioritaire" />
      )}
      {leger.length > 0 && <Etage sorte="leger" nom={ETAGE_LEGER} lignes={leger} onOuvrir={onOuvrir} tuto="pendant-tete" />}
      {plusLourd.length > 0 && <Etage sorte="plus-lourd" nom={ETAGE_PLUS_LOURD} lignes={plusLourd} onOuvrir={onOuvrir} />}
      {nombreSousLEau > 0 && (
        <SousLEau lignes={sousLEau} nombre={nombreSousLEau} ouvert={ouvert} onBasculer={onBasculer} onOuvrir={onOuvrir} />
      )}
    </section>
  );
}

function Etage({
  sorte,
  nom,
  lignes,
  onOuvrir,
  tuto,
}: {
  sorte: Sorte;
  nom: string;
  lignes: LigneDuPourquoi[];
  onOuvrir: (id: string) => void;
  tuto?: string;
}) {
  return (
    <section className={`etage etage--${sorte}`} aria-label={nom} data-tuto={tuto}>
      <p className="etage-regle">{nom}</p>
      <div className={`etage-galets etage-galets--${lignes.length}`}>
        {lignes.map((l) => (
          <Galet key={l.id} ligne={l} prioritaire={sorte === 'surface'} onOuvrir={onOuvrir} />
        ))}
      </div>
    </section>
  );
}

function Galet({
  ligne,
  prioritaire,
  onOuvrir,
}: {
  ligne: LigneDuPourquoi;
  prioritaire: boolean;
  onOuvrir: (id: string) => void;
}) {
  const mention = mentionDuGalet(ligne);
  return (
    <button
      type="button"
      className={`galet${prioritaire ? ' galet--prioritaire' : ''}`}
      title={ligne.titre}
      style={{ ['--teinte' as string]: teinteDuProjet(ligne.dossier?.couleur) }}
      onClick={() => onOuvrir(ligne.id)}
    >
      <b>{ligne.titre}</b>
      {mention && <small>{mention}</small>}
    </button>
  );
}

function SousLEau({
  lignes,
  nombre,
  ouvert,
  onBasculer,
  onOuvrir,
}: {
  lignes: LigneDuPourquoi[];
  nombre: number;
  ouvert: boolean;
  onBasculer: () => void;
  onOuvrir: (id: string) => void;
}) {
  const { montres, autres } = nomsSousLEau(lignes, nombre, ouvert);
  return (
    <section className="etage etage--sous-l-eau" aria-label={ETAGE_SOUS_L_EAU} data-tuto="sous-l-eau">
      <p className="etage-regle">{ETAGE_SOUS_L_EAU}</p>
      <div className="etage-noms">
        {montres.map((l) => (
          <button key={l.id} type="button" className="etage-nom" title={l.titre} onClick={() => onOuvrir(l.id)}>
            {l.titre}
          </button>
        ))}
        {!ouvert && autres > 0 && (
          <button type="button" className="etage-nom etage-nom--autres" aria-expanded={false} onClick={onBasculer}>
            {nAutres(autres)}
          </button>
        )}
        {ouvert && (
          <button type="button" className="etage-nom etage-nom--autres" aria-expanded onClick={onBasculer}>
            {REPLIER}
          </button>
        )}
      </div>
    </section>
  );
}
